/**
 * Every cache key in the portal.
 *
 * Keys are built here and nowhere else, because TanStack matches them by
 * prefix and that only helps if the prefixes are designed. The portal
 * previously built keys inline as `[resource, { page, search, fixedQuery }]`,
 * which made invalidation either an exact match or a guess — and gave a
 * room-type mutation no way to say "the property this belongs to changed".
 *
 * The shape is always `[resource, kind, ...identity]`, so:
 *
 *   invalidate(['properties'])                 every property query
 *   invalidate(['properties', 'detail', 'p1']) one property and its children
 */

import type { QueryKey } from '@tanstack/react-query';

export interface ListParams {
  // Explicit `| undefined` throughout: with `exactOptionalPropertyTypes`, an
  // omitted key and one set to `undefined` are different types, and callers
  // building this object from optional local values (a search box that was
  // cleared, a sort that was reset) legitimately produce the latter.
  // `canonicalListParams` treats both the same way — it drops them.
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
  readonly q?: string | undefined;
  readonly sortBy?: string | undefined;
  readonly sortDirection?: string | undefined;
  /** Selected column fields. Sorted, so column order cannot fragment the cache. */
  readonly fields?: readonly string[] | undefined;
  /** Page-fixed parameters, e.g. the property a room list belongs to. */
  readonly scope?: Readonly<Record<string, string>> | undefined;
}

/**
 * Normalises list parameters into one canonical form.
 *
 * Two things would otherwise fragment the cache into entries that hold the
 * same rows. Object key order changes the serialised key, so `{page, q}` and
 * `{q, page}` become two entries. And an empty search is expressible three
 * ways — `''`, `undefined`, absent — each its own entry.
 */
export function canonicalListParams(params: ListParams): ListParams {
  const entries = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== '')
    .sort(([left], [right]) => left.localeCompare(right));

  const canonical: Record<string, unknown> = {};
  for (const [key, value] of entries) {
    // Sorted rather than preserved: the set of fields is what identifies the
    // request, and the order a reader happened to tick the boxes is not part
    // of it. Also what keeps a widened selection from matching the narrower
    // selection's entry once server projection exists.
    canonical[key] = key === 'fields' ? [...(value as readonly string[])].sort() : value;
  }

  return canonical;
}

interface ResourceQueryKeys {
  readonly all: QueryKey;
  readonly lists: QueryKey;
  readonly details: QueryKey;
  readonly list: (params: ListParams) => QueryKey;
  readonly detail: (id: string) => QueryKey;
  readonly options: (params: { readonly q?: string | undefined }) => QueryKey;
}

function resourceKeys(resource: string): ResourceQueryKeys {
  return {
    all: [resource] as const,
    /**
     * Every list of this resource, and every detail of it.
     *
     * The two prefixes a write invalidates. `all` would do it in one key and
     * is deliberately not what a mutation uses: reference vocabularies are
     * keyed under `[resource, 'reference', …]` and are prefixed by `all`
     * too, so invalidating the root refetches two hundred and fifty countries
     * every time somebody saves a form.
     */
    lists: [resource, 'list'] as const,
    details: [resource, 'detail'] as const,
    list: (params: ListParams) => [resource, 'list', canonicalListParams(params)] as const,
    detail: (id: string) => [resource, 'detail', id] as const,
    /**
     * The picker's options, under `reference` rather than `list`.
     *
     * A write invalidates `lists` and `details`; the options list is the same
     * data seen through a different lens, but it is small, cached hard and read
     * by forms in other modules, so it sits outside both prefixes and is
     * invalidated explicitly by the module that owns it.
     */
    options: (params: { readonly q?: string | undefined }) =>
      [resource, 'reference', 'options', params.q ?? ''] as const,
  };
}

/**
 * The reference vocabularies, keyed once.
 *
 * Under their own root rather than under each consuming module: they are the
 * same rows whoever asked, and two roots meant a property save and a tenant
 * save each refetched their own copy of two hundred and fifty countries.
 */
const reference = {
  currencies: ['reference', 'currencies'] as const,
  countries: ['reference', 'countries'] as const,
  states: (countryCode: string) => ['reference', 'states', countryCode] as const,
  cities: (countryCode: string, stateCode: string) =>
    ['reference', 'cities', countryCode, stateCode] as const,
} as const;

export const queryKeys = {
  amenities: resourceKeys('amenities'),
  inclusions: resourceKeys('inclusions'),
  pmsCredentials: resourceKeys('pmsCredentials'),
  properties: {
    ...resourceKeys('properties'),
    // Nested under the parent detail so invalidating one property reaches its
    // children. A sibling key would leave a stale room-type list behind a
    // property that has just been edited.
    roomTypes: (id: string) => ['properties', 'detail', id, 'room-types'] as const,
    rooms: (id: string) => ['properties', 'detail', id, 'rooms'] as const,
    // The picker every property-scoped module binds to. Under 'reference'
    // because saving a policy must not refetch the property list.
    options: (q: string) => ['properties', 'reference', 'options', q] as const,
  },
  reference,
  reservations: resourceKeys('reservations'),
  guests: resourceKeys('guests'),
  users: resourceKeys('users'),
  roles: resourceKeys('roles'),
  audit: resourceKeys('audit'),
  roomTypes: resourceKeys('roomTypes'),
  rooms: resourceKeys('rooms'),
  ratePlans: resourceKeys('ratePlans'),
  dailyRates: resourceKeys('dailyRates'),
  tenantAdmins: resourceKeys('tenantAdmins'),
  cancellationPolicies: {
    ...resourceKeys('cancellation-policies'),
    // Under 'reference', outside the 'list' and 'detail' prefixes a write
    // invalidates: a picker's contents do not change because somebody renamed
    // an unrelated policy on another property.
    options: (propertyId: string) =>
      ['cancellation-policies', 'reference', 'options', propertyId] as const,
  },
  paymentPolicies: {
    ...resourceKeys('payment-policies'),
    options: (propertyId: string) =>
      ['payment-policies', 'reference', 'options', propertyId] as const,
  },
  promotions: resourceKeys('promotions'),
  rules: resourceKeys('rules'),
  tenants: {
    ...resourceKeys('tenants'),
    // Under 'reference' with the location cascade, not under 'list' or
    // 'detail': the catalogue is the same for every tenant and changes only
    // when the platform ships a module, so invalidating a tenant must not
    // refetch it.
    modules: ['tenants', 'reference', 'modules'] as const,
    attachmentCredentials: (tenantId: string) =>
      ['tenants', 'attachment-credentials', tenantId] as const,
  },
  currencies: resourceKeys('currencies'),
  countries: resourceKeys('countries'),
  states: resourceKeys('states'),
  cities: resourceKeys('cities'),
  availability: resourceKeys('availability'),
  notificationGateways: resourceKeys('notification-gateways'),
  notificationTemplates: resourceKeys('notification-templates'),
  notificationTemplateTags: ['notification-template-tags'] as const,
  paymentGateways: resourceKeys('paymentGateways'),
  propertyGroups: resourceKeys('propertyGroups'),
  taxes: resourceKeys('taxes'),
  exchangeRates: resourceKeys('exchangeRates'),
  reservationSummary: (params: {
    readonly from: string;
    readonly to: string;
    readonly q: string;
  }) => ['reservation-summary', params.from, params.to, params.q] as const,
  dashboard: { all: ['dashboard'] as const },
} as const;
